import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api, { ESTADOS, PRIORIDADES, formatoMXN } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, Save, Plus, Trash2, Package, Search } from 'lucide-react';
import { toast } from 'sonner';
import { hoyLocal } from '@/lib/fechas';

export default function OrdenFormulario() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const esEdicion = !!id;

  const [clientes, setClientes] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [vehiculosFiltrados, setVehiculosFiltrados] = useState([]);
  const [mecanicos, setMecanicos] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [form, setForm] = useState({
    cliente_id: searchParams.get('cliente_id') || '',
    vehiculo_id: '',
    mecanico_id: '',
    fecha_ingreso: hoyLocal(),
    problema_reportado: '',
    diagnostico: '',
    reparacion_realizada: '',
    costo_mano_obra: '',
    descuento: '',
    prioridad: 'media',
    estado: 'recibido',
    incluir_iva: false,
    notas_internas: '',
    notas_cliente: '',
    fecha_promesa: '',
  });
  const [partes, setPartes] = useState([]);
  const [inventario, setInventario] = useState([]);
  const [busquedaInv, setBusquedaInv] = useState({});
  const [dropdownIdx, setDropdownIdx] = useState(null);
  const [tasaImpuesto, setTasaImpuesto] = useState(16);
  const [nombreImpuesto, setNombreImpuesto] = useState('IVA');

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/configuracion');
        if (r.data) {
          setTasaImpuesto(parseFloat(r.data.tasa_impuesto ?? 16));
          setNombreImpuesto(r.data.nombre_impuesto || 'IVA');
        }
      } catch { /* silent */ }
    })();
  }, []);

  // Fetch inventory for autocomplete
  const buscarInventario = async (q, idx) => {
    setBusquedaInv(prev => ({ ...prev, [idx]: q }));
    try {
      const res = await api.get(`/inventario/buscar?q=${encodeURIComponent(q)}`);
      setInventario(res.data);
      setDropdownIdx(idx);
    } catch { setInventario([]); }
  };

  const seleccionarProducto = (idx, producto) => {
    setPartes(prev => prev.map((p, i) => i === idx ? {
      ...p,
      descripcion: producto.nombre,
      precio_unitario: producto.precio_venta || '',
      inventario_id: producto.id,
      sku: producto.sku || '',
      stock_disponible: producto.stock,
    } : p));
    setBusquedaInv(prev => ({ ...prev, [idx]: '' }));
    setDropdownIdx(null);
    setInventario([]);
  };

  const seleccionarOtro = (idx, texto) => {
    setPartes(prev => prev.map((p, i) => i === idx ? {
      ...p,
      descripcion: texto,
      inventario_id: '',
      sku: '',
      stock_disponible: null,
    } : p));
    setDropdownIdx(null);
    setInventario([]);
    setBusquedaInv(prev => ({ ...prev, [idx]: '' }));
  };

  // Close dropdown on click outside (with delay to allow clicking items)
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('[data-inv-dropdown]')) {
        setTimeout(() => setDropdownIdx(null), 150);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const cargar = async () => {
      try {
        const [cRes, vRes, mRes] = await Promise.all([
          api.get('/clientes'),
          api.get('/vehiculos'),
          api.get('/usuarios/mecanicos')
        ]);
        setClientes(cRes.data);
        setVehiculos(vRes.data);
        setMecanicos(mRes.data);

        if (esEdicion) {
          const oRes = await api.get(`/ordenes/${id}`);
          const o = oRes.data;
          setForm({
            cliente_id: o.cliente_id || '',
            vehiculo_id: o.vehiculo_id || '',
            mecanico_id: o.mecanico_id || '',
            fecha_ingreso: o.fecha_ingreso || '',
            problema_reportado: o.problema_reportado || '',
            diagnostico: o.diagnostico || '',
            reparacion_realizada: o.reparacion_realizada || '',
            costo_mano_obra: o.costo_mano_obra || '',
            descuento: o.descuento || '',
            prioridad: o.prioridad || 'media',
            estado: o.estado || 'recibido',
            incluir_iva: o.incluir_iva || false,
            notas_internas: o.notas_internas || '',
            notas_cliente: o.notas_cliente || '',
            fecha_promesa: o.fecha_promesa || '',
          });
          if (o.partes && o.partes.length > 0) {
            setPartes(o.partes.map(p => ({
              id: p.id,
              descripcion: p.descripcion || '',
              cantidad: p.cantidad || 1,
              precio_unitario: p.precio_unitario || 0,
              inventario_id: p.inventario_id || '',
              sku: p.sku || '',
              stock_disponible: null,
            })));
          }
        }
      } catch { toast.error('Error al cargar datos'); }
    };
    cargar();
  }, [id, esEdicion]);

  useEffect(() => {
    if (form.cliente_id) {
      setVehiculosFiltrados(vehiculos.filter(v => v.cliente_id === form.cliente_id));
    } else {
      setVehiculosFiltrados(vehiculos);
    }
  }, [form.cliente_id, vehiculos]);

  const costoPartes = partes.reduce((sum, p) => sum + (parseFloat(p.cantidad) || 0) * (parseFloat(p.precio_unitario) || 0), 0);
  const subtotal = (parseFloat(form.costo_mano_obra) || 0) + costoPartes;
  const subtotalConDescuento = subtotal - (parseFloat(form.descuento) || 0);
  const iva = form.incluir_iva ? subtotalConDescuento * (tasaImpuesto / 100) : 0;
  const total = subtotalConDescuento + iva;

  const agregarParte = () => {
    setPartes([...partes, { id: crypto.randomUUID(), descripcion: '', cantidad: 1, precio_unitario: '', inventario_id: '', sku: '', stock_disponible: null }]);
  };

  const actualizarParte = (idx, campo, valor) => {
    setPartes(prev => prev.map((p, i) => i === idx ? { ...p, [campo]: valor } : p));
  };

  const eliminarParte = (idx) => {
    setPartes(prev => prev.filter((_, i) => i !== idx));
  };

  const guardar = async () => {
    if (!form.cliente_id) { toast.error('Selecciona un cliente'); return; }
    if (!form.problema_reportado) { toast.error('Describe el problema reportado'); return; }
    setGuardando(true);
    try {
      const payload = { ...form, partes, costo_partes: costoPartes, iva: iva };
      if (esEdicion) {
        await api.put(`/ordenes/${id}`, payload);
        toast.success('Orden actualizada');
        navigate(`/ordenes/${id}`);
      } else {
        const res = await api.post('/ordenes', payload);
        toast.success('Orden creada exitosamente');
        navigate(`/ordenes/${res.data.id}`);
      }
    } catch { toast.error('Error al guardar orden'); }
    finally { setGuardando(false); }
  };

  const set = (campo, valor) => setForm(prev => ({ ...prev, [campo]: valor }));

  return (
    <div className="space-y-6 animate-fadeIn max-w-4xl" data-testid="orden-formulario-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(esEdicion ? `/ordenes/${id}` : '/ordenes')} className="text-slate-400 hover:text-white" data-testid="volver-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-barlow text-3xl font-bold uppercase tracking-tight text-slate-50">
          {esEdicion ? 'Editar Orden' : 'Nueva Orden de Trabajo'}
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Card className="bg-slate-900/40 border-slate-800">
            <CardHeader className="pb-3"><CardTitle className="font-barlow text-lg text-slate-200">Información General</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label className="text-xs uppercase tracking-wider text-slate-400">Cliente *</Label>
                  <Select value={form.cliente_id} onValueChange={v => { set('cliente_id', v); set('vehiculo_id', ''); }}>
                    <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="orden-cliente-select"><SelectValue placeholder="Seleccionar cliente" /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      {clientes.map(c => <SelectItem key={c.id} value={c.id} className="text-slate-300">{c.nombre_completo}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs uppercase tracking-wider text-slate-400">Vehículo</Label>
                  <Select value={form.vehiculo_id} onValueChange={v => set('vehiculo_id', v)}>
                    <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="orden-vehiculo-select"><SelectValue placeholder="Seleccionar vehiculo" /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      {vehiculosFiltrados.map(v => <SelectItem key={v.id} value={v.id} className="text-slate-300">{v.marca} {v.modelo} {v.anio} - {v.placas}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs uppercase tracking-wider text-slate-400">Mecánico Asignado</Label>
                  <Select value={form.mecanico_id} onValueChange={v => set('mecanico_id', v)}>
                    <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="orden-mecanico-select"><SelectValue placeholder="Seleccionar mecanico" /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      {mecanicos.map(m => <SelectItem key={m.id} value={m.id} className="text-slate-300">{m.nombre}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs uppercase tracking-wider text-slate-400">Prioridad</Label>
                  <Select value={form.prioridad} onValueChange={v => set('prioridad', v)}>
                    <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="orden-prioridad-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      {Object.entries(PRIORIDADES).map(([k, v]) => <SelectItem key={k} value={k} className="text-slate-300">{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs uppercase tracking-wider text-slate-400">Fecha Ingreso</Label>
                  <Input type="date" value={form.fecha_ingreso} onChange={e => set('fecha_ingreso', e.target.value)}
                    className="bg-slate-950 border-slate-700 text-slate-200" data-testid="orden-fecha-ingreso-input" /></div>
                <div><Label className="text-xs uppercase tracking-wider text-slate-400">Fecha Promesa</Label>
                  <Input type="date" value={form.fecha_promesa} onChange={e => set('fecha_promesa', e.target.value)}
                    className="bg-slate-950 border-slate-700 text-slate-200" data-testid="orden-fecha-promesa-input" /></div>
              </div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Problema Reportado *</Label>
                <Textarea value={form.problema_reportado} onChange={e => set('problema_reportado', e.target.value)}
                  className="bg-slate-950 border-slate-700 text-slate-200" rows={3} data-testid="orden-problema-input" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Diagnóstico</Label>
                <Textarea value={form.diagnostico} onChange={e => set('diagnostico', e.target.value)}
                  className="bg-slate-950 border-slate-700 text-slate-200" rows={2} data-testid="orden-diagnostico-input" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Reparación Realizada</Label>
                <Textarea value={form.reparacion_realizada} onChange={e => set('reparacion_realizada', e.target.value)}
                  className="bg-slate-950 border-slate-700 text-slate-200" rows={2} data-testid="orden-reparacion-input" /></div>
            </CardContent>
          </Card>

          {/* Refacciones / Insumos */}
          <Card className="bg-slate-900/40 border-slate-800">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-barlow text-lg text-slate-200 flex items-center gap-2">
                  <Package className="w-5 h-5 text-amber-500" /> Refacciones e Insumos
                </CardTitle>
                <Button onClick={agregarParte} size="sm" className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm" data-testid="agregar-parte-btn">
                  <Plus className="w-4 h-4 mr-1" /> Agregar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {partes.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-6">Sin refacciones agregadas. Usa el boton "Agregar" para incluir piezas.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-800 hover:bg-transparent">
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500">Producto / Refacción</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 w-20">Cant.</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 w-32">Precio Unit.</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 w-28 text-right">Subtotal</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {partes.map((p, idx) => {
                      const sub = (parseFloat(p.cantidad) || 0) * (parseFloat(p.precio_unitario) || 0);
                      const textoInput = busquedaInv[idx] !== undefined ? busquedaInv[idx] : '';
                      return (
                        <TableRow key={p.id} className="border-slate-800/50" data-testid={`parte-row-${idx}`}>
                          <TableCell className="p-1 relative">
                            {p.inventario_id ? (
                              <div className="flex items-center gap-1">
                                <span className="text-sm text-slate-200 truncate flex-1">
                                  {p.sku && <span className="text-xs font-mono text-slate-500 mr-1">[{p.sku}]</span>}
                                  {p.descripcion}
                                </span>
                                <button onClick={() => { actualizarParte(idx, 'descripcion', ''); actualizarParte(idx, 'inventario_id', ''); actualizarParte(idx, 'precio_unitario', ''); }}
                                  className="text-slate-500 hover:text-red-400 shrink-0"><Trash2 className="w-3 h-3" /></button>
                              </div>
                            ) : (
                              <div className="relative" data-inv-dropdown="true">
                                <div className="relative">
                                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
                                  <Input
                                    value={p.descripcion || textoInput}
                                    onChange={e => {
                                      const v = e.target.value;
                                      actualizarParte(idx, 'descripcion', v);
                                      buscarInventario(v, idx);
                                    }}
                                    onFocus={() => buscarInventario(p.descripcion || '', idx)}
                                    placeholder="Buscar en inventario..."
                                    className="bg-slate-950 border-slate-700 text-slate-200 text-sm h-9 pl-7"
                                    data-testid={`parte-desc-${idx}`}
                                  />
                                </div>
                                {dropdownIdx === idx && inventario.length > 0 && (
                                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-slate-900 border border-slate-700 rounded-md shadow-xl max-h-48 overflow-y-auto" data-inv-dropdown="true" data-testid={`inv-dropdown-${idx}`}>
                                    {inventario.map(prod => (
                                      <button key={prod.id} onMouseDown={e => e.preventDefault()} onClick={() => seleccionarProducto(idx, prod)}
                                        className="w-full text-left px-3 py-2 hover:bg-slate-800 flex items-center justify-between gap-2 text-sm"
                                        data-testid={`inv-item-${prod.id}`}>
                                        <div className="truncate">
                                          <span className="text-slate-200">{prod.nombre}</span>
                                          {prod.sku && <span className="text-slate-500 ml-1 text-xs">({prod.sku})</span>}
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0 text-xs">
                                          <span className="text-amber-500 font-mono">${prod.precio_venta}</span>
                                          <span className={`${prod.stock > 0 ? 'text-emerald-400' : 'text-red-400'}`}>{prod.stock} disp.</span>
                                        </div>
                                      </button>
                                    ))}
                                    <button onMouseDown={e => e.preventDefault()} onClick={() => seleccionarOtro(idx, p.descripcion || textoInput)}
                                      className="w-full text-left px-3 py-2 hover:bg-slate-800 text-sm border-t border-slate-700 text-indigo-400"
                                      data-testid={`inv-otro-${idx}`}>
                                      + Otro (manual)
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                            {p.inventario_id && p.stock_disponible !== null && (
                              <p className="text-[10px] text-emerald-500 mt-0.5">{p.stock_disponible} en stock</p>
                            )}
                          </TableCell>
                          <TableCell className="p-1">
                            <Input type="number" min="1" step="1" value={p.cantidad} onChange={e => actualizarParte(idx, 'cantidad', e.target.value)}
                              className="bg-slate-950 border-slate-700 text-slate-200 text-sm h-9 font-mono" data-testid={`parte-cant-${idx}`} />
                          </TableCell>
                          <TableCell className="p-1">
                            <Input type="number" step="0.01" value={p.precio_unitario} onChange={e => actualizarParte(idx, 'precio_unitario', e.target.value)}
                              placeholder="0.00" readOnly={!!p.inventario_id}
                              className={`border-slate-700 text-sm h-9 font-mono ${p.inventario_id ? 'bg-slate-900 text-amber-400' : 'bg-slate-950 text-slate-200'}`}
                              data-testid={`parte-precio-${idx}`} />
                          </TableCell>
                          <TableCell className="p-1 text-right">
                            <span className="text-sm text-amber-500 font-mono font-semibold">{formatoMXN(sub)}</span>
                          </TableCell>
                          <TableCell className="p-1">
                            <Button variant="ghost" size="icon" onClick={() => eliminarParte(idx)} className="text-red-400 hover:text-red-300 h-8 w-8" data-testid={`eliminar-parte-${idx}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
              {partes.length > 0 && (
                <div className="flex justify-end mt-3 pt-3 border-t border-slate-800">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-slate-400">Total Refacciones:</span>
                    <span className="text-lg font-bold text-amber-500 font-mono">{formatoMXN(costoPartes)}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-slate-900/40 border-slate-800">
            <CardHeader className="pb-3"><CardTitle className="font-barlow text-lg text-slate-200">Notas</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Notas Internas</Label>
                <Textarea value={form.notas_internas} onChange={e => set('notas_internas', e.target.value)}
                  className="bg-slate-950 border-slate-700 text-slate-200" rows={2} data-testid="orden-notas-internas-input" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Notas para el Cliente</Label>
                <Textarea value={form.notas_cliente} onChange={e => set('notas_cliente', e.target.value)}
                  className="bg-slate-950 border-slate-700 text-slate-200" rows={2} data-testid="orden-notas-cliente-input" /></div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="bg-slate-900/40 border-slate-800">
            <CardHeader className="pb-3"><CardTitle className="font-barlow text-lg text-slate-200">Costos</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Mano de Obra (MXN)</Label>
                <Input type="number" step="0.01" value={form.costo_mano_obra} onChange={e => set('costo_mano_obra', e.target.value)}
                  className="bg-slate-950 border-slate-700 text-slate-200 font-mono" data-testid="orden-mano-obra-input" /></div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Refacciones</Label>
                <div className="bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-slate-300 font-mono text-sm">
                  {formatoMXN(costoPartes)}
                </div>
                <p className="text-[10px] text-slate-600 mt-1">{partes.length} pieza{partes.length !== 1 ? 's' : ''} agregada{partes.length !== 1 ? 's' : ''}</p>
              </div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Descuento (MXN)</Label>
                <Input type="number" step="0.01" value={form.descuento} onChange={e => set('descuento', e.target.value)}
                  className="bg-slate-950 border-slate-700 text-slate-200 font-mono" data-testid="orden-descuento-input" /></div>
              <div className="flex items-center justify-between py-2 px-1 border border-slate-700 rounded-md cursor-pointer hover:bg-slate-800/30"
                onClick={() => set('incluir_iva', !form.incluir_iva)} data-testid="toggle-iva-btn">
                <span className="text-xs uppercase tracking-wider text-slate-400">Incluir {nombreImpuesto} ({tasaImpuesto}%)</span>
                <div className={`w-10 h-5 rounded-full transition-colors relative ${form.incluir_iva ? 'bg-amber-500' : 'bg-slate-700'}`}>
                  <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.incluir_iva ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </div>
              </div>
              <div className="border-t border-slate-800 pt-3 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span className="text-slate-300 font-mono">{formatoMXN(subtotal)}</span></div>
                {(parseFloat(form.descuento) || 0) > 0 && (
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Descuento</span><span className="text-green-400 font-mono">-{formatoMXN(parseFloat(form.descuento) || 0)}</span></div>
                )}
                {form.incluir_iva && (
                  <div className="flex justify-between text-sm"><span className="text-slate-500">{nombreImpuesto} {tasaImpuesto}%</span><span className="text-slate-300 font-mono">{formatoMXN(iva)}</span></div>
                )}
                <div className="flex justify-between text-sm font-bold"><span className="text-slate-200">Total</span><span className="text-amber-500 font-mono">{formatoMXN(Math.max(total, 0))}</span></div>
              </div>
            </CardContent>
          </Card>

          {esEdicion && (
            <Card className="bg-slate-900/40 border-slate-800">
              <CardHeader className="pb-3"><CardTitle className="font-barlow text-lg text-slate-200">Estado</CardTitle></CardHeader>
              <CardContent>
                <Select value={form.estado} onValueChange={v => set('estado', v)}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="orden-estado-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    {Object.entries(ESTADOS).map(([k, v]) => <SelectItem key={k} value={k} className="text-slate-300">{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
          )}

          <Button onClick={guardar} disabled={guardando} className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm h-11 shadow-[0_0_15px_rgba(245,158,11,0.3)]" data-testid="guardar-orden-btn">
            <Save className="w-4 h-4 mr-2" />
            {guardando ? 'Guardando...' : esEdicion ? 'Actualizar Orden' : 'Crear Orden'}
          </Button>
        </div>
      </div>
    </div>
  );
}
